<script lang="ts">
	import Info from '@lucide/svelte/icons/info'
	import StarPlus from '@lucide/svelte/icons/star-plus'
	import TriangleAlert from '@lucide/svelte/icons/triangle-alert'
	import { useSearchParams } from 'runed/kit'

	import { goto } from '$app/navigation'
	import { resolve } from '$app/paths'
	import ComboBox from '#lib/components/combo-box.svelte'
	import CtaButton from '#lib/components/cta-button.svelte'
	import MetricLineChartCard from '#lib/components/metric-line-chart-card.svelte'
	import OpenInAi from '#lib/components/open-in-ai.svelte'
	import RangeDropdown from '#lib/components/range-dropdown.svelte'
	import * as Accordion from '#lib/components/ui/accordion/index.js'
	import * as Alert from '#lib/components/ui/alert/index.js'
	import Badge from '#lib/components/ui/badge/badge.svelte'
	import { Button } from '#lib/components/ui/button/index.js'
	import * as Card from '#lib/components/ui/card/index.js'
	import { LISTED_CHAINS } from '#lib/constants/chainsConfig.js'
	import { CHAINS_SOCIALS } from '#lib/constants/chainsSocials.js'
	import { m } from '#lib/paraglide/messages.js'
	import { isChainPos, getToken, isChainNew } from '#lib/utils/chainsHelpers.js'
	import { updateGetListedDialog } from '#lib/utils/dialogs.svelte.js'
	import {
		formatTps,
		formatTime,
		formatFinality,
		formatType,
		formatTxns,
		formatLaunchDate,
		formatGovernance,
		formatCount,
		formatStakeOrHashrate,
		formatValidatorsOrMiners,
		formatConsensus,
		formatDevelopers,
		formatRepos,
		formatCommits,
		formatStars,
		formatWatchers,
		formatRevenue,
		formatAvgTxFee,
		formatPrice,
		formatMarketCapToFdv,
		formatMetricName,
		formatAgents,
		formatUsd,
	} from '#lib/utils/formatters.js'
	import {
		AGENTS_ACTIVITY_RANGE_OPTIONS,
		DEV_ACTIVITY_RANGE_OPTIONS,
		FINANCIALS_RANGE_OPTIONS,
		SCALABILITY_RANGE_OPTIONS,
	} from '#lib/utils/metricsRanges.js'
	import { n } from '#lib/utils/navigation.svelte.js'
	import { phg } from '#lib/utils/posthog.js'
	import { chainSchema } from '#lib/utils/schema.js'
	import {
		getNcTooltip,
		getMaxTpsTooltip,
		getTpsTooltip,
		isChainWithSpecialOneBlockMaxTps,
		getStakeOrHashrateTooltip,
		getValidatorsOrMinersTooltip,
		getBlocktimeTooltip,
		getLaunchedTooltip,
		getGovernanceTooltip,
		getDevelopersTooltip,
		getReposTooltip,
		getCommitsTooltip,
		getStarsTooltip,
		getWatchersTooltip,
		getTotalTxnsTooltip,
		getTxnsTooltip,
		getRevenueTooltip,
		getAvgTxFeeTooltip,
		getPriceTooltip,
		getMarketCapTooltip,
		getFdvTooltip,
		getMarketCapToFdvTooltip,
	} from '#lib/utils/tooltips.js'

	import MetricCard from './components/metric-card.svelte'

	let { data } = $props()

	const params = useSearchParams(chainSchema, { noScroll: true })

	let isStakeInUsd = $derived(isChainPos(data.chain) && params.stake === 'usd')
</script>

<section class="container max-w-280 px-2 py-3 sm:p-6">
	<CtaButton class="mb-5 w-full font-extrabold sm:mb-7" size="xl" />

	<div
		class="mb-5 flex flex-col flex-wrap items-stretch justify-between gap-4 sm:mb-7 md:flex-row md:items-end"
	>
		<div class="flex items-center justify-start gap-1.5">
			<div class="relative flex items-center justify-start gap-1.5">
				<img
					class="xs:size-12 size-9.5"
					alt="{data.chain.name} logo"
					crossorigin="anonymous"
					decoding="async"
					fetchpriority="low"
					height="48"
					loading="lazy"
					src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{data.chain.slug}.svg"
					width="48"
				/>

				<h1 class="truncate text-2xl font-extrabold md:text-4xl lg:text-5xl">
					{#if data.chain.id === 'bitcoin_chain'}
						{data.chain.name} TPS
					{:else if data.chain.id === 'hedera_chain'}
						{data.chain.name} Transactions
					{:else}
						{data.chain.name}
					{/if}
				</h1>

				{#if isChainNew(data.chain)}
					<Badge
						class="absolute -top-2 right-3 translate-x-full font-bold uppercase"
						size="3xs"
						variant="brand"
					>
						New
					</Badge>
				{/if}
			</div>
		</div>

		<div class="flex items-center justify-start gap-2">
			<ComboBox
				class="min-w-40 flex-1 sm:w-auto sm:min-w-48"
				disabled={n.l}
				onChange={value => {
					const selectedChain = LISTED_CHAINS.find(c => c.id === value)

					if (selectedChain) {
						phg.capture('change_current_chain', {
							from: data.chain.id,
							to: selectedChain.id,
						})

						goto(
							resolve('/(app)/chain/[slug]', {
								slug: selectedChain.slug,
							})
						)
					}
				}}
				options={LISTED_CHAINS.map(({ name, id, slug }) => ({
					label: name,
					value: id,
					slug,
				}))}
				placeholder="Select chain"
				value={data.chain.id}
			/>

			<OpenInAi
				prompt="I’m looking at this blockchain on Chainspect: https://chainspect.app/chain/{data
					.chain
					.slug}. What stands out the most about its scalability, decentralization, developer activity, or financials?"
			/>
		</div>
	</div>

	{#if data.chain.network === 'testnet'}
		<Alert.Root class="mb-5 sm:mb-7" variant="warning">
			<TriangleAlert class="h-4 w-4" size="16" />

			<Alert.Description>
				This page displays data for a testnet, used for development and experimentation. Activity
				and performance may differ from real-world conditions on mainnet.
			</Alert.Description>
		</Alert.Root>
	{/if}

	{#if data.chain.id === 'bitcoin_chain'}
		<Alert.Root class="mb-5 sm:mb-7">
			<Info />

			<Alert.Title tag="h2">
				Why Is Bitcoin Often Said to Process 7 Transactions per Second?
			</Alert.Title>

			<Alert.Description>
				Bitcoin’s commonly cited 7 TPS is a legacy theoretical estimate: a 1,000,000-byte block
				divided by an assumed 250-byte average transaction and a 600-second block interval equals
				approximately 6.7 TPS. Actual TPS varies with transaction size, SegWit usage, batching and
				block timing. For current performance, view Bitcoin’s live TPS and real-time network metrics
				below.
			</Alert.Description>
		</Alert.Root>
	{/if}

	<div class="relative">
		{#if !data.chain.listed}
			<div class="bg-background/10 absolute -inset-1 z-1 rounded-lg backdrop-blur-xs">
				<div class="sticky top-0 flex items-center justify-center pt-40 pb-2">
					<Button
						onclick={() => {
							updateGetListedDialog({
								open: true,
								chainName: data.chain.name,
								chainSocials: CHAINS_SOCIALS[data.chain.id],
							})
							phg.capture('get_listed_dialog_open', { place: `${data.chain.name} page` })
						}}
						size="lg"
						variant="glowing"
					>
						<StarPlus />

						{m.menu_get_listed_long()}
					</Button>
				</div>
			</div>
		{/if}

		<div class="mb-3 flex items-center justify-between gap-2">
			<h2 class="relative w-fit text-lg leading-none font-semibold tracking-tight">
				{#if data.chain.id === 'hedera_chain'}
					{data.chain.name} TPS Live, Transactions & Finality Time
				{:else}
					{data.chain.name} TPS, Block Time & Finality Time
				{/if}
			</h2>

			<RangeDropdown
				description={m.scalability_range_dropdown_description()}
				options={SCALABILITY_RANGE_OPTIONS}
				size="default"
				bind:value={params['range-cm']}
			/>
		</div>

		<div class="mb-5 grid gap-2 sm:mb-7 sm:grid-cols-2 sm:gap-3 md:grid-cols-3 md:gap-4">
			<MetricCard
				name={formatMetricName(
					params['range-cm'],
					m.metric_tps_name_short,
					m.metric_tps_name_short_range
				)}
				chain={data.chain}
				change={data.chain.tps?.change}
				metric="tps"
				popoverContent={getTpsTooltip(data.chain)}
				showIcon={params.s}
				value={formatTps(data.chain.tps?.value)}
			/>

			<MetricCard
				name={params['max-tps'] === 'hundred-blocks'
					? m.metric_max_tps_hundred_blocks_name()
					: m.metric_max_tps_one_block_name()}
				actionState={params['max-tps'] === 'one-block'}
				actionText={params['max-tps'] === 'hundred-blocks'
					? m.metric_max_tps_switch_to_one_block()
					: m.metric_max_tps_switch_to_hundred_blocks()}
				chain={data.chain}
				metric="max_tps"
				onActionClick={() => {
					params['max-tps'] =
						params['max-tps'] === 'hundred-blocks' ? 'one-block' : 'hundred-blocks'

					phg.capture('max_tps_column_state_change', { new_state: params['max-tps'] })
				}}
				popoverContent={getMaxTpsTooltip(data.chain, params['max-tps'])}
				popoverType={isChainWithSpecialOneBlockMaxTps(data.chain.id, params['max-tps'])
					? 'warning'
					: 'info'}
				showIcon={params.s}
				value={formatTps(
					params['max-tps'] === 'hundred-blocks'
						? data.chain.maxTps?.value
						: data.chain.maxTpsOneBlock?.value
				)}
			/>

			<MetricCard
				name={m.metric_theor_tps_name()}
				chain={data.chain}
				metric="theor_tps"
				showIcon={params.s}
				value={formatTps(data.chain.theorTps)}
			/>

			<MetricCard
				name={m.metric_ttf_name()}
				chain={data.chain}
				metric="ttf"
				showIcon={params.s}
				value={formatFinality(data.chain.ttf)}
			/>

			<MetricCard
				name={formatMetricName(
					params['range-cm'],
					m.metric_blocktime_name,
					m.metric_blocktime_name_range
				)}
				chain={data.chain}
				metric="blocktime"
				popoverContent={getBlocktimeTooltip(data.chain)}
				showIcon={params.s}
				value={formatTime(data.chain.blocktime)}
			/>

			<MetricCard
				name={formatMetricName(params['range-cm'], m.metric_txns_name, m.metric_txns_name_range)}
				chain={data.chain}
				change={data.chain.tps?.change}
				metric="txns"
				popoverContent={getTxnsTooltip(data.chain)}
				showIcon={params.s}
				value={formatTxns(data.chain.txns, 'short', 'compact')}
			/>

			<MetricCard
				name={m.metric_total_txns_name()}
				chain={data.chain}
				metric="total_txns"
				popoverContent={getTotalTxnsTooltip(data.chain)}
				showIcon={params.s}
				value={formatTxns(data.chain.totalTxns?.value, 'short', 'compact')}
			/>

			<MetricCard
				name={m.metric_type_name()}
				chain={data.chain}
				showIcon={params.s}
				value={formatType(data.chain.type, false)}
			/>

			<MetricCard
				name={params['launched'] === 'date'
					? m.metric_launch_date_name()
					: m.metric_launch_age_name()}
				actionState={params['launched'] === 'age'}
				actionText={params['launched'] === 'date'
					? m.metric_launch_switch_to_age()
					: m.metric_launch_switch_to_date()}
				chain={data.chain}
				metric="launch_date_age"
				onActionClick={() => {
					params['launched'] = params['launched'] === 'date' ? 'age' : 'date'

					phg.capture('launched_column_state_change', { new_state: params['launched'] })
				}}
				popoverContent={getLaunchedTooltip(data.chain)}
				showIcon={params.s}
				value={formatLaunchDate(data.chain.launched, params['launched'])}
			/>
		</div>

		<h2 class="relative mb-4 w-fit text-lg leading-none font-semibold tracking-tight">
			{data.chain.name} Nakamoto Coefficient & {isChainPos(data.chain) ? 'Validators' : 'Miners'}
		</h2>

		<div class="mb-5 grid gap-2 sm:mb-7 sm:grid-cols-2 sm:gap-3 md:grid-cols-3 md:gap-4">
			<MetricCard
				name={m.metric_nc_name()}
				chain={data.chain}
				change={data.chain.nc?.change}
				metric="nc"
				popoverContent={getNcTooltip(data.chain)}
				showIcon={params.s}
				value={formatCount(data.chain.nc?.value)}
			/>

			<MetricCard
				name={isChainPos(data.chain) ? m.metric_validators_name() : m.metric_miners_name()}
				chain={data.chain}
				change={data.chain.validatorsOrMiners?.change}
				metric={isChainPos(data.chain) ? 'validators' : 'miners'}
				popoverContent={getValidatorsOrMinersTooltip(data.chain)}
				showIcon={params.s}
				value={formatValidatorsOrMiners(data.chain.validatorsOrMiners?.value, data.chain)}
			/>

			<MetricCard
				name={isChainPos(data.chain) ? m.metric_stake_name() : m.metric_hashrate_name()}
				chain={data.chain}
				change={isStakeInUsd ? data.chain.stakeUsd?.change : data.chain.stakeOrHashrate?.change}
				metric={isChainPos(data.chain) ? 'stake' : 'hashrate'}
				popoverContent={getStakeOrHashrateTooltip(data.chain)}
				showIcon={params.s}
				value={formatStakeOrHashrate(
					isStakeInUsd ? data.chain.stakeUsd?.value : data.chain.stakeOrHashrate?.value,
					data.chain,
					params['stake']
				)}
				{...isChainPos(data.chain) && data.chain.stakeOrHashrate?.value != null
					? {
							actionState: params['stake'] === 'native',
							actionText:
								params['stake'] === 'usd'
									? m.metric_stake_switch_to_native_token({
											token: getToken(data.chain, 'stake'),
										})
									: m.metric_stake_switch_to_usd(),
							onActionClick: () => {
								params['stake'] = params['stake'] === 'usd' ? 'native' : 'usd'

								phg.capture('stake_or_hashrate_column_state_change', {
									new_state: params['stake'],
								})
							},
						}
					: {}}
			/>

			<MetricCard
				name={m.metric_consensus_name()}
				chain={data.chain}
				metric="consensus"
				showIcon={params.s}
				value={formatConsensus(data.chain.consensus, 'short')}
			/>

			<MetricCard
				name={m.metric_governance_name()}
				chain={data.chain}
				metric="governance"
				popoverContent={getGovernanceTooltip(data.chain)}
				showIcon={params.s}
				value={formatGovernance(data.chain.governance)}
			/>
		</div>

		<div class="mb-3 flex items-center justify-between gap-2">
			<h2 class="relative w-fit text-lg leading-none font-semibold tracking-tight">
				{data.chain.name} Developer Activity
			</h2>

			<RangeDropdown
				options={DEV_ACTIVITY_RANGE_OPTIONS}
				size="default"
				bind:value={params['range-dm']}
			/>
		</div>

		<div class="mb-5 grid gap-2 sm:mb-7 sm:grid-cols-2 sm:gap-3 md:grid-cols-3 md:gap-4">
			<MetricCard
				name={formatMetricName(
					params['range-dm'],
					m.metric_developers_name,
					m.metric_developers_name_range
				)}
				chain={data.chain}
				change={data.chain.developers?.change}
				metric="developers"
				popoverContent={getDevelopersTooltip(data.chain)}
				showIcon={params.s}
				value={formatDevelopers(data.chain.developers?.value)}
			/>

			<MetricCard
				name={formatMetricName(
					params['range-dm'],
					m.metric_commits_name,
					m.metric_commits_name_range
				)}
				chain={data.chain}
				change={data.chain.commits?.change}
				metric="commits"
				popoverContent={getCommitsTooltip(data.chain)}
				showIcon={params.s}
				value={formatCommits(data.chain.commits?.value)}
			/>

			<MetricCard
				name={formatMetricName(params['range-dm'], m.metric_repos_name, m.metric_repos_name_range)}
				chain={data.chain}
				change={data.chain.repos?.change}
				metric="repos"
				popoverContent={getReposTooltip(data.chain)}
				showIcon={params.s}
				value={formatRepos(data.chain.repos?.value)}
			/>

			<MetricCard
				name={formatMetricName(params['range-dm'], m.metric_stars_name, m.metric_stars_name_range)}
				chain={data.chain}
				change={data.chain.stars?.change}
				metric="stars"
				popoverContent={getStarsTooltip(data.chain)}
				showIcon={params.s}
				value={formatStars(data.chain.stars?.value)}
			/>

			<MetricCard
				name={formatMetricName(
					params['range-dm'],
					m.metric_watchers_name,
					m.metric_watchers_name_range
				)}
				chain={data.chain}
				change={data.chain.watchers?.change}
				metric="watchers"
				popoverContent={getWatchersTooltip(data.chain)}
				showIcon={params.s}
				value={formatWatchers(data.chain.watchers?.value)}
			/>
		</div>

		<div class="mb-3 flex items-center justify-between gap-2">
			<h2 class="relative w-fit text-lg leading-none font-semibold tracking-tight">
				{data.chain.name} Fees & Market Cap
			</h2>

			<RangeDropdown
				description={m.financials_range_dropdown_description()}
				options={FINANCIALS_RANGE_OPTIONS}
				size="default"
				bind:value={params['range-fm']}
			/>
		</div>

		<div class="mb-5 grid gap-2 sm:mb-7 sm:grid-cols-2 sm:gap-3 md:grid-cols-3 md:gap-4">
			<MetricCard
				name={formatMetricName(
					params['range-fm'],
					m.metric_revenue_name,
					m.metric_revenue_name_range
				)}
				actionState={params['revenue'] === 'native'}
				actionText={params['revenue'] === 'usd'
					? m.metric_revenue_switch_to_native_token()
					: m.metric_revenue_switch_to_usd()}
				chain={data.chain}
				change={params['revenue'] === 'usd'
					? data.chain.revenueUsd?.change
					: data.chain.revenue?.change}
				metric="revenue"
				onActionClick={() => {
					params['revenue'] = params['revenue'] === 'usd' ? 'native' : 'usd'

					phg.capture('revenue_column_state_change', { new_state: params['revenue'] })
				}}
				popoverContent={getRevenueTooltip(data.chain)}
				showIcon={params.s}
				value={formatRevenue(
					params['revenue'] === 'usd' ? data.chain.revenueUsd?.value : data.chain.revenue?.value,
					data.chain,
					params['revenue']
				)}
			/>

			<MetricCard
				name={formatMetricName(
					params['range-fm'],
					m.metric_avg_tx_fee_name,
					m.metric_avg_tx_fee_name_range
				)}
				actionState={params['avgTxFee'] === 'native'}
				actionText={params['avgTxFee'] === 'usd'
					? m.metric_avg_tx_fee_switch_to_native_token()
					: m.metric_avg_tx_fee_switch_to_usd()}
				chain={data.chain}
				change={params['avgTxFee'] === 'usd'
					? data.chain.avgTxFeeUsd?.change
					: data.chain.avgTxFee?.change}
				inverseChange={true}
				metric="avg_tx_fee"
				onActionClick={() => {
					params['avgTxFee'] = params['avgTxFee'] === 'usd' ? 'native' : 'usd'

					phg.capture('avg_tx_fee_column_state_change', { new_state: params['avgTxFee'] })
				}}
				popoverContent={getAvgTxFeeTooltip(data.chain)}
				showIcon={params.s}
				value={formatAvgTxFee(
					params['avgTxFee'] === 'usd' ? data.chain.avgTxFeeUsd?.value : data.chain.avgTxFee?.value,
					data.chain,
					params['avgTxFee']
				)}
			/>

			<MetricCard
				name={m.metric_market_cap_name()}
				chain={data.chain}
				change={data.chain.marketCap?.change}
				metric="market_cap"
				popoverContent={getMarketCapTooltip(data.chain)}
				showIcon={params.s}
				value={formatUsd(data.chain.marketCap?.value)}
			/>

			<MetricCard
				name={m.metric_fdv_name()}
				chain={data.chain}
				change={data.chain.fdv?.change}
				metric="fdv"
				popoverContent={getFdvTooltip(data.chain)}
				showIcon={params.s}
				value={formatUsd(data.chain.fdv?.value)}
			/>

			<MetricCard
				name={m.metric_market_cap_to_fdv_name()}
				chain={data.chain}
				change={data.chain.marketCapToFdv?.change}
				metric="market_cap_to_fdv"
				popoverContent={getMarketCapToFdvTooltip(data.chain)}
				showIcon={params.s}
				value={formatMarketCapToFdv(data.chain.marketCapToFdv?.value)}
			/>

			<MetricCard
				name={m.metric_price_name()}
				chain={data.chain}
				change={data.chain.price?.change}
				metric="price"
				popoverContent={getPriceTooltip(data.chain)}
				showIcon={params.s}
				value={formatPrice(data.chain.price?.value)}
			/>
		</div>

		{#if data.chain.agents != null}
			<div class="mb-3 flex items-center justify-between gap-2">
				<h2 class="relative w-fit text-lg leading-none font-semibold tracking-tight">
					{data.chain.name} AI agents

					<Badge class="absolute -top-2 -right-8 uppercase" size="3xs" variant="brand">New</Badge>
				</h2>

				<RangeDropdown
					options={AGENTS_ACTIVITY_RANGE_OPTIONS}
					size="default"
					bind:value={params['range-am']}
				/>
			</div>

			<div class="mb-5 grid gap-2 sm:mb-7 sm:grid-cols-2 sm:gap-3 md:grid-cols-3 md:gap-4">
				<MetricCard
					name={formatMetricName(
						params['range-am'],
						m.metric_agents_name,
						m.metric_agents_name_range
					)}
					chain={data.chain}
					change={data.chain.agents?.change}
					showIcon={params.s}
					value={formatAgents(data.chain.agents?.value)}
				/>

				<MetricCard
					name={formatMetricName(
						params['range-am'],
						m.metric_active_agents_name,
						m.metric_active_agents_name_range
					)}
					chain={data.chain}
					change={data.chain.activeAgents?.change}
					showIcon={params.s}
					value={formatAgents(data.chain.activeAgents?.value)}
				/>

				<MetricCard
					name={formatMetricName(params['range-am'], m.metric_x402_name, m.metric_x402_name_range)}
					chain={data.chain}
					change={data.chain.x402?.change}
					metric="agent_x402"
					showIcon={params.s}
					value={formatAgents(data.chain.x402?.value)}
				/>

				<MetricCard
					name={formatMetricName(
						params['range-am'],
						m.metric_services_n_name,
						m.metric_services_n_name_range
					)}
					chain={data.chain}
					change={data.chain.servicesN?.change}
					metric="services_n"
					showIcon={params.s}
					value={formatAgents(data.chain.servicesN?.value)}
				/>

				<MetricCard
					name={formatMetricName(
						params['range-am'],
						m.metric_services_name,
						m.metric_services_name_range
					)}
					chain={data.chain}
					metric="agent_services"
					popoverContent={data.chain.services && data.chain.services.length > 2
						? data.chain.services
								.slice(2)
								.map(s => s.name.toUpperCase())
								.join(', ')
						: null}
					showIcon={params.s}
					value={data.chain.services
						? data.chain.services
								.slice(0, 2)
								.map(s => s.name.toUpperCase())
								.join(', ')
						: '—'}
				/>
			</div>
		{/if}
	</div>

	{#if data.chartData}
		<MetricLineChartCard
			chains={[data.chain]}
			chartData={data.chartData}
			{params}
			title={data.chain.name}
		/>
	{/if}

	{#if data.chain.listed}
		<Card.Root class="mt-5">
			<Card.Content>
				<Accordion.Root type="single">
					<Accordion.Item value="item-1">
						<Accordion.Trigger>Compare {data.chain.name} With Other Blockchains</Accordion.Trigger>

						<Accordion.Content>
							<ul>
								{#each LISTED_CHAINS.filter(({ id }) => id !== data.chain.id) as chain (chain.id)}
									<li>
										<a
											class="text-primary hover:underline"
											href={resolve('/(app)/compare/[slug]', {
												slug: `${data.chain.slug}-vs-${chain.slug}`,
											})}
										>
											{data.chain.name} vs {chain.name}
										</a>
									</li>
								{/each}
							</ul>
						</Accordion.Content>
					</Accordion.Item>
				</Accordion.Root>
			</Card.Content>
		</Card.Root>
	{/if}
</section>
